import React, { useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import Hero from "../components/Hero";
import Gallery from "../components/Gallery";
import StudioStrip from "../components/StudioStrip";
import CustomSections from "../components/CustomSections";
import Footer from "../components/Footer";
import { useContent } from "../lib/content";

export default function HomePage() {
  const { content } = useContent();
  const mainRef = useRef(null);
  const enabled = Boolean(
    content?.scroll_effect_enabled || content?.custom_sections_carousel3d_enabled
  );
  const mode = content?.scroll_effect_mode || "carousel3d";
  const intensityRaw = Number(
    content?.scroll_effect_intensity ?? content?.custom_sections_carousel3d_intensity
  );
  const intensity = Number.isFinite(intensityRaw)
    ? Math.min(100, Math.max(0, intensityRaw))
    : 42;

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return undefined;

    const resetSections = () => {
      root.querySelectorAll(".scroll-effect-section").forEach((section) => {
        section.style.removeProperty("transform");
        section.style.removeProperty("opacity");
        section.style.removeProperty("filter");
        section.style.removeProperty("z-index");
      });
    };

    if (!enabled) {
      resetSections();
      return undefined;
    }

    let raf = 0;
    const transformFor = (distance, abs) => {
      if (mode === "scale") {
        const scale = 1 - abs * intensity * 0.0035;
        return `translateY(${distance * intensity * -0.55}px) scale(${Math.max(0.72, scale)})`;
      }
      if (mode === "cover") {
        const scale = 1 - abs * 0.08;
        return `translateY(${distance * intensity * -1.1}px) scale(${Math.max(0.82, scale)})`;
      }
      if (mode === "page") {
        return `perspective(1400px) rotateX(${distance * intensity * 0.28}deg) translateY(${distance * intensity * -0.65}px)`;
      }
      return `perspective(1400px) translateX(${distance * intensity * -0.68}px) translateZ(${
        (1 - abs) * 54
      }px) rotateY(${distance * intensity * -0.42}deg) rotateX(${distance * intensity * 0.08}deg)`;
    };

    const update = () => {
      raf = 0;
      const viewportMid = window.innerHeight / 2;
      root.querySelectorAll(".scroll-effect-section").forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        const sectionMid = rect.top + rect.height / 2;
        const distance = (sectionMid - viewportMid) / Math.max(window.innerHeight, 1);
        const clamped = Math.max(-1, Math.min(1, distance));
        section.style.setProperty("--scroll-effect", String(clamped));
        section.style.setProperty("--scroll-effect-abs", String(Math.abs(clamped)));
        section.style.setProperty("--scroll-effect-intensity", String(intensity));
        section.style.setProperty("--scroll-effect-index", String(index));
        section.style.transform = transformFor(clamped, Math.abs(clamped));
        section.style.opacity = String(Math.max(0.62, 1 - Math.abs(clamped) * 0.34));
      });
    };
    const requestUpdate = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      resetSections();
    };
  }, [enabled, intensity, mode]);

  return (
    <main
      ref={mainRef}
      data-testid="home-page"
      className={enabled ? `scroll-effects scroll-effect-${mode}` : undefined}
      style={{ "--scroll-effect-intensity": intensity }}
    >
      <Navbar />
      <Hero />
      <Gallery />
      <StudioStrip />
      <CustomSections />
      <Footer />
    </main>
  );
}
